import React from 'react';
import { Link, useParams } from 'react-router-dom';
import PageHero from '../components/PageHero';
import { useTranslation } from '../i18nContext';
import { isValidLocale } from '../i18n';

const ARCO_EMAIL = 'cmic@cmicmexico.org';
const SITE_URL = 'https://cmicmexico.org/';
const SITE_LABEL = 'cmicmexico.org';
const LAST_UPDATE = '20/07/2021';

export default function PrivacyPage() {
  const { lang } = useParams();
  const t = useTranslation();
  const locale = (lang && isValidLocale(lang)) ? lang : t.locale;
  const isEs = locale === 'es';

  return (
    <div data-testid="privacy-page">
      <PageHero
        label={isEs ? 'Documento legal CMIC' : 'CMIC Legal Document'}
        title={isEs ? 'Aviso de privacidad integral' : 'Privacy Notice'}
      />

      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-charcoal-700">

          <p className="text-[15px] leading-7" data-testid="privacy-intro">
            <strong>Colegio Mexicano de Intérpretes de Conferencias A.C.</strong>, mejor conocido como
            Colegio Mexicano de Intérpretes de Conferencias, con domicilio en Río Tigris núm. 44, interior 7,
            Colonia Cuauhtémoc, Alcaldía Cuauhtémoc, C.P. 06500, Ciudad de México, y portal de internet{' '}
            <a href={SITE_URL} target="_blank" rel="noopener noreferrer" className="text-cmic-700 hover:underline">{SITE_LABEL}</a>,
            es el responsable del uso y protección de sus datos personales, y al respecto le informamos lo siguiente.
          </p>

          <Section id="fines" title="¿Para qué fines utilizaremos sus datos personales?">
            <p>Los datos personales que recabamos de usted los utilizaremos para las siguientes finalidades, que son necesarias para el servicio que solicita:</p>
            <ul>
              <li>Respuesta a mensajes del formulario de contacto.</li>
              <li>Prestación de cualquier servicio solicitado.</li>
            </ul>
          </Section>

          <Section id="datos" title="¿Qué datos personales utilizaremos para estos fines?">
            <p>Para llevar a cabo las finalidades descritas en el presente aviso de privacidad, utilizaremos los siguientes datos personales:</p>
            <ul>
              <li>Datos de identificación y contacto.</li>
            </ul>
          </Section>

          <Section id="arco" title="¿Cómo puede acceder, rectificar o cancelar sus datos personales, oponerse a su uso o revocar el consentimiento?">
            <p>
              Usted tiene derecho a conocer qué datos personales tenemos de usted, para qué los utilizamos y las
              condiciones del uso que les damos (Acceso). Asimismo, es su derecho solicitar la corrección de su
              información personal en caso de que esté desactualizada, sea inexacta o incompleta (Rectificación);
              que la eliminemos de nuestros registros o bases de datos cuando considere que la misma no está siendo
              utilizada adecuadamente (Cancelación); así como oponerse al uso de sus datos personales para fines
              específicos (Oposición). Estos derechos se conocen como derechos ARCO.
            </p>
            <p>
              Para el ejercicio de cualquiera de los derechos ARCO, debe enviar una petición vía correo electrónico a{' '}
              <a href={`mailto:${ARCO_EMAIL}?subject=Derechos%20ARCO`} data-testid="privacy-arco-email" className="text-cmic-700 hover:underline">{ARCO_EMAIL}</a>{' '}
              y deberá contener:
            </p>
            <ul>
              <li>Nombre completo del titular.</li>
              <li>Domicilio.</li>
              <li>Teléfono.</li>
              <li>Correo electrónico usado en este sitio web.</li>
              <li>Copia de una identificación oficial adjunta.</li>
              <li>Asunto «Derechos ARCO».</li>
            </ul>
            <p>
              Descripción del objeto del escrito, los cuales pueden ser de manera enunciativa más no limitativa los
              siguientes: revocación del consentimiento para tratar sus datos personales; y/o notificación del uso
              indebido del tratamiento de sus datos personales; y/o ejercitar sus derechos ARCO, con una descripción
              clara y precisa de los datos a Acceder, Rectificar, Cancelar o bien, Oponerse. En caso de rectificación
              de datos personales, deberá indicar la modificación exacta y anexar la documentación soporte. Es
              importante, en caso de revocación del consentimiento, que tenga en cuenta que no en todos los casos
              podremos atender su solicitud o concluir el uso de forma inmediata, ya que es posible que por alguna
              obligación legal requiramos seguir tratando sus datos personales. Asimismo, deberá considerar que para
              ciertos fines la revocación de su consentimiento implicará que no le podamos seguir prestando el servicio
              que nos solicitó, o la conclusión de su relación con nosotros.
            </p>
            <p className="font-semibold text-charcoal-800 mt-5 mb-1">¿En cuántos días le daremos respuesta a su solicitud?</p>
            <p>20 días.</p>
            <p className="font-semibold text-charcoal-800 mt-4 mb-1">¿Por qué medio le comunicaremos la respuesta a su solicitud?</p>
            <p>Al mismo correo electrónico desde donde se envió la petición.</p>
          </Section>

          <Section id="cookies" title="El uso de tecnologías de rastreo en nuestro portal de internet">
            <p>
              Le informamos que en nuestra página de internet utilizamos cookies, web beacons u otras tecnologías, a
              través de las cuales es posible monitorear su comportamiento como usuario de internet, así como brindarle
              un mejor servicio y experiencia al navegar en nuestra página. Los datos personales que obtenemos de estas
              tecnologías de rastreo son los siguientes:
            </p>
            <ul>
              <li>Identificadores, nombre de usuario y contraseñas de sesión.</li>
              <li>Región en la que se encuentra el usuario.</li>
              <li>Tipo de navegador del usuario.</li>
              <li>Fecha y hora del inicio y final de una sesión de un usuario.</li>
            </ul>
            <p>
              Estas cookies, web beacons y otras tecnologías pueden ser deshabilitadas. Para conocer cómo hacerlo,
              consulte el menú de ayuda de su navegador. Tenga en cuenta que, en caso de desactivar las cookies, es
              posible que no pueda acceder a ciertas funciones personalizadas en nuestro sitio web.
            </p>
          </Section>

          <Section id="cambios" title="¿Cómo puede conocer los cambios en este aviso de privacidad?">
            <p>
              El presente aviso de privacidad puede sufrir modificaciones, cambios o actualizaciones derivadas de
              nuevos requerimientos legales; de nuestras propias necesidades por los productos o servicios que
              ofrecemos; de nuestras prácticas de privacidad; de cambios en nuestro modelo de negocio, o por otras
              causas. Nos comprometemos a mantener actualizado este aviso de privacidad sobre los cambios que pueda
              sufrir y siempre podrá consultar las actualizaciones que existan en el sitio web{' '}
              <a href={SITE_URL} target="_blank" rel="noopener noreferrer" className="text-cmic-700 hover:underline">{SITE_LABEL}</a>.
            </p>
          </Section>

          <p className="text-sm text-charcoal-500 mt-10 pt-6 border-t border-charcoal-100">
            Última actualización de este aviso de privacidad: {LAST_UPDATE}
          </p>

          <p className="text-sm text-charcoal-500 mt-4" data-testid="privacy-cta">
            Para cualquier duda sobre el tratamiento de sus datos personales, puede escribirnos a{' '}
            <a href={`mailto:${ARCO_EMAIL}`} className="text-cmic-700 hover:underline">{ARCO_EMAIL}</a>{' '}
            o a través de nuestro{' '}
            <Link to={`/${locale}/${isEs ? 'contacto' : 'contact'}`} data-testid="privacy-cta-button" className="text-cmic-700 hover:underline">formulario de contacto</Link>.
          </p>

        </div>
      </section>
    </div>
  );
}

function Section({ id, title, children }) {
  return (
    <section id={id} className="scroll-mt-28 mt-10" data-testid={`privacy-section-${id}`}>
      <h2 className="text-lg font-bold text-charcoal-800 mb-3 leading-snug">{title}</h2>
      <div className="space-y-3 text-[15px] leading-7 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-1.5 [&_ul]:mt-2">
        {children}
      </div>
    </section>
  );
}
